/**
 * API Client
 *
 * Fetch wrapper mit Error Handling
 */

const API_BASE = '/api';

// Default Timeout für alle API-Requests (8 Sekunden)
const DEFAULT_TIMEOUT_MS = 8000;

export interface ApiError {
  error: string;
  code?: string;
  retryAfter?: number;
}

export class ApiException extends Error {
  constructor(
    public status: number,
    public data: ApiError,
  ) {
    super(data.error);
  }
}

/**
 * Timeout-fähiger Fetch-Wrapper
 * Garantiert, dass der Promise IMMER resolved oder rejected wird (nie pending forever)
 */
async function request<T>(
  path: string,
  options: RequestInit = {},
  timeoutMs: number = DEFAULT_TIMEOUT_MS,
): Promise<T> {
  const method = options.method || 'GET';

  // AbortController für Timeout
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(`${API_BASE}${path}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      credentials: 'include',
      signal: controller.signal,
    });

    const contentType = response.headers.get('content-type') || '';

    // Content-Type prüfen vor JSON-Parsing (Server könnte HTML liefern bei Redirect/Error)
    if (!contentType.includes('application/json')) {
      // Server liefert kein JSON - wahrscheinlich HTML Error Page oder Redirect
      const text = await response.text();
      console.error(
        '[api] Non-JSON response:',
        response.status,
        contentType,
        text.substring(0, 200),
      );
      throw new ApiException(response.status, {
        error: 'Server-Fehler (kein JSON)',
        code: 'INVALID_RESPONSE',
      });
    }

    const data = await response.json();

    if (!response.ok) {
      console.error('[api] error response', { status: response.status, data, ts: Date.now() });
      throw new ApiException(response.status, data);
    }

    return data as T;
  } catch (err) {
    // AbortError = Timeout
    if (err instanceof Error && err.name === 'AbortError') {
      console.error('[api] timeout', { path, ts: Date.now() });
      throw new ApiException(0, {
        error: 'Zeitüberschreitung',
        code: 'TIMEOUT',
      });
    }
    // Bereits ApiException - durchreichen
    if (err instanceof ApiException) {
      throw err;
    }
    // Netzwerkfehler oder JSON-Parse-Error
    console.error('[api] network error', { path, err, ts: Date.now() });
    throw new ApiException(0, {
      error: 'Verbindungsfehler',
      code: 'NETWORK_ERROR',
    });
  } finally {
    clearTimeout(timeoutId);
  }
}

// =============================================================================
// AUTH
// =============================================================================

export interface LoginResponse {
  success: boolean;
  requiresTwoFactor: boolean;
  requiresTwoFactorSetup: boolean;
}

export async function login(email: string, password: string): Promise<LoginResponse> {
  return request<LoginResponse>('/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });
}

export interface TwoFactorSetupResponse {
  qrCodeDataUrl: string;
  secret: string;
}

export async function setup2FA(): Promise<TwoFactorSetupResponse> {
  return request<TwoFactorSetupResponse>('/auth/2fa/setup', {
    method: 'POST',
  });
}

export async function verify2FA(code: string): Promise<{ success: boolean }> {
  return request<{ success: boolean }>('/auth/2fa/verify', {
    method: 'POST',
    body: JSON.stringify({ code }),
  });
}

export async function logout(): Promise<void> {
  await request('/auth/logout', { method: 'POST' });
}

export interface MeResponse {
  userId: string;
  displayName: string;
  role: string;
  twoFactorVerified: boolean;
  twoFactorEnabled: boolean;
  stationId?: string;
  stationType?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  /** Station-only Mode: Station gekoppelt aber kein Worker eingeloggt */
  stationOnly?: boolean;
}

export async function getMe(): Promise<MeResponse> {
  return request<MeResponse>('/auth/me');
}

// =============================================================================
// VERSION (öffentlich, ohne Login)
// =============================================================================

export interface VersionResponse {
  /** CalVer aus der VERSION-Datei, z.B. "2026.8.0" */
  version: string;
  /** Commit-Kurz-SHA des Builds oder "lokal" */
  commit: string;
  /** Build-Zeitpunkt als ISO-String, null wenn unbekannt */
  builtAt: string | null;
}

export async function getVersion(): Promise<VersionResponse> {
  return request<VersionResponse>('/version');
}

export interface UpdateProfileRequest {
  displayName?: string;
  firstName?: string;
  lastName?: string;
}

export interface UpdateProfileResponse {
  success: boolean;
  user: {
    displayName: string;
    firstName: string | null;
    lastName: string | null;
  };
}

export async function updateProfile(data: UpdateProfileRequest): Promise<UpdateProfileResponse> {
  return request<UpdateProfileResponse>('/auth/me', {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

// =============================================================================
// INVITE + 2FA MANAGEMENT
// =============================================================================

export async function sendInvite(userId: string): Promise<{ success: boolean }> {
  return request<{ success: boolean }>('/auth/invite', {
    method: 'POST',
    body: JSON.stringify({ userId }),
  });
}

export async function disable2FA(password: string): Promise<{ success: boolean }> {
  return request<{ success: boolean }>('/auth/2fa/disable', {
    method: 'POST',
    body: JSON.stringify({ password }),
  });
}

export async function reconfigure2FA(password: string): Promise<{ success: boolean }> {
  return request<{ success: boolean }>('/auth/2fa/reconfigure', {
    method: 'POST',
    body: JSON.stringify({ password }),
  });
}

export async function resetUser2FA(userId: string): Promise<{ success: boolean }> {
  return request<{ success: boolean }>(`/admin/users/${userId}/reset-2fa`, {
    method: 'POST',
  });
}

// =============================================================================
// EMAIL CHANGE
// =============================================================================

export async function changeEmail(email: string): Promise<{ success: boolean }> {
  return request<{ success: boolean }>('/auth/change-email', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}

export async function verifyEmail(token: string): Promise<{ success: boolean }> {
  return request<{ success: boolean }>('/auth/verify-email', {
    method: 'POST',
    body: JSON.stringify({ token }),
  });
}

// =============================================================================
// PASSWORD RESET + INVITE
// =============================================================================

export async function forgotPassword(email: string): Promise<{ success: boolean }> {
  return request<{ success: boolean }>('/auth/forgot-password', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}

export async function resetPassword(
  token: string,
  password: string,
): Promise<{ success: boolean }> {
  return request<{ success: boolean }>('/auth/reset-password', {
    method: 'POST',
    body: JSON.stringify({ token, password }),
  });
}

export async function setPasswordViaInvite(
  token: string,
  password: string,
): Promise<{ success: boolean }> {
  return request<{ success: boolean }>('/auth/set-password', {
    method: 'POST',
    body: JSON.stringify({ token, password }),
  });
}

// =============================================================================
// STATION
// =============================================================================

export interface StationPairResponse {
  success: boolean;
  station: {
    id: string;
    name: string | null;
    type: string;
    location: string | null;
  };
}

export interface StationStatusResponse {
  id: string;
  name: string | null;
  type: string;
  location: string | null;
  isActive: boolean;
  unlockTtlSeconds?: number;
  activeWorker?: {
    userId: string;
    displayName: string;
    unlockedAt: string;
    expiresIn: number;
  };
}

export async function getStationStatus(): Promise<StationStatusResponse> {
  return request<StationStatusResponse>('/station/status');
}

export interface RfidLoginResponse {
  success: boolean;
  worker: {
    userId: string;
    displayName: string;
  };
  expiresIn: number;
  redirectUrl: string;
}

export async function rfidLogin(chipId: string): Promise<RfidLoginResponse> {
  return request<RfidLoginResponse>('/station/rfid', {
    method: 'POST',
    body: JSON.stringify({ chipId }),
  });
}

export async function extendUnlock(
  userId: string,
): Promise<{ success: boolean; expiresIn: number }> {
  return request<{ success: boolean; expiresIn: number }>('/station/unlock', {
    method: 'POST',
    body: JSON.stringify({ userId }),
  });
}

export async function stationLogout(): Promise<{ success: boolean }> {
  return request<{ success: boolean }>('/station/logout', {
    method: 'POST',
  });
}

// =============================================================================
// STATION PAIRING (QR-Code Flow)
// =============================================================================

export interface PairingInitResponse {
  token: string;
  expiresAt: string;
}

export async function initPairing(deviceHint?: string): Promise<PairingInitResponse> {
  return request<PairingInitResponse>('/station/init', {
    method: 'POST',
    body: JSON.stringify({ deviceHint }),
  });
}

export interface PairingClaimResponse {
  status: 'pending' | 'paired';
  stationId?: string;
  stationType?: string;
}

export async function claimPairing(token: string): Promise<PairingClaimResponse> {
  const response = await fetch(`${API_BASE}/station/claim`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ token }),
  });

  const data = await response.json();

  // 202 Accepted = pending (nicht als Fehler behandeln)
  if (response.status === 202) {
    return { status: 'pending' };
  }

  if (!response.ok) {
    throw new ApiException(response.status, data);
  }

  return data as PairingClaimResponse;
}

export interface AdminPairingApproveResponse {
  success: boolean;
  stationId: string;
}

export async function adminApprovePairing(
  token: string,
  type: 'WSA' | 'VORARBEITER' | 'ADMIN',
  name?: string,
  location?: string,
): Promise<AdminPairingApproveResponse> {
  return request<AdminPairingApproveResponse>('/admin/stations/pair', {
    method: 'POST',
    body: JSON.stringify({ token, type, name, location }),
  });
}

// =============================================================================
// WSA ENTRIES (VS#11)
// =============================================================================

export interface WsaEntryResponse {
  id: string;
  machineId: string;
  machine: { name: string; capacityKg: number };
  customerId: string | null;
  customerGroupId: string | null;
  customer: { name: string; shortName: string | null } | null;
  customerGroup: { id: string; name: string; type: string } | null;
  programId: string | null;
  program: { number: string; name: string } | null;
  weightKg: number;
  recordedAt: string;
  shiftDate: string;
  shiftType: string;
  remarks: string | null;
  recordedBy: { displayName: string };
  washRunId: string | null;
  participants: Array<{ id: string; customer: { name: string; shortName: string | null } }>;
  // V2 Hardening: Server-State für Badge-Logik
  hasOpenCorrection?: boolean;
  openCorrectionId?: string | null;
  /**
   * Gesetzt, wenn eine Korrektur angenommen wurde. Die Felder oben tragen dann
   * bereits den korrigierten Stand; hier steht, was sich geändert hat.
   */
  correction?: KorrekturHinweis | null;
}

/** Angenommene Korrektur — was sich gegenüber der Erfassung geändert hat. */
export interface KorrekturHinweis {
  gewichtVorher: number | null;
  felder: string[];
}

export interface CreateWsaEntryRequest {
  machineId: string;
  customerId: string;
  programId: string;
  weightKg: number;
  remarks?: string;
  washRunId?: string;
  /** Karenz 18.08.: bewusste Nachzügler-Buchung in die eben beendete Schicht. */
  vorherigeSchicht?: boolean;
}

export async function createWsaEntry(
  stationId: string | null,
  data: CreateWsaEntryRequest,
): Promise<WsaEntryResponse> {
  // Admin ohne Station: stationlosen Endpoint nutzen
  if (!stationId) {
    return request<WsaEntryResponse>('/wsa-entries', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }
  return request<WsaEntryResponse>(`/stations/${stationId}/wsa-entries`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function getWsaEntries(
  stationId: string | null,
  range: string = 'today',
): Promise<{ entries: WsaEntryResponse[] }> {
  // Admin ohne Station: stationlosen Endpoint nutzen
  if (!stationId) {
    return request<{ entries: WsaEntryResponse[] }>(`/wsa-entries?range=${range}`);
  }
  return request<{ entries: WsaEntryResponse[] }>(
    `/stations/${stationId}/wsa-entries?range=${range}`,
  );
}

// =============================================================================
// PROCESSING ENTRIES (VS#12)
// =============================================================================

export interface ProcessingEntryResponse {
  id: string;
  lineId: string;
  line: { name: string };
  subLineId: string | null;
  subLine: { name: string } | null;
  customerId: string | null;
  customerGroupId: string | null;
  customer: { name: string; shortName: string | null } | null;
  customerGroup: { name: string } | null;
  weightKg: number;
  recordedAt: string;
  shiftDate: string;
  shiftType: string;
  remarks: string | null;
  recordedBy: { displayName: string };
  laundryClass: 'HOTEL' | 'HEALTHCARE' | 'RESIDENT' | null; // DEPRECATED
  laundryClassId: string | null;
  laundryClassName: string | null;
  articleGroupId: string | null;
  articleGroupName: string | null;
  // V2 Hardening: Server-State fuer Badge-Logik
  hasOpenCorrection?: boolean;
  openCorrectionId?: string | null;
  openCorrectionPreview?: {
    items: Array<{ field: string; oldValue: unknown; newValue: unknown }>;
    workerComment: string | null;
  };
  /** Siehe WsaEntryResponse.correction. */
  correction?: KorrekturHinweis | null;
}

export interface CreateProcessingEntryRequest {
  lineId: string;
  subLineId?: string;
  customerId: string;
  weightKg: number;
  remarks?: string;
  laundryClassId?: string; // NEU: FK auf LaundryClassEntity
}

export async function createProcessingEntry(
  stationId: string | null,
  data: CreateProcessingEntryRequest,
): Promise<ProcessingEntryResponse> {
  // Admin ohne Station: stationlosen Endpoint nutzen
  if (!stationId) {
    return request<ProcessingEntryResponse>('/processing-entries', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }
  return request<ProcessingEntryResponse>(`/stations/${stationId}/processing-entries`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function getProcessingEntries(
  stationId: string | null,
  range: string = 'today',
): Promise<{ entries: ProcessingEntryResponse[] }> {
  // Admin ohne Station: stationlosen Endpoint nutzen
  if (!stationId) {
    return request<{ entries: ProcessingEntryResponse[] }>(`/processing-entries?range=${range}`);
  }
  return request<{ entries: ProcessingEntryResponse[] }>(
    `/stations/${stationId}/processing-entries?range=${range}`,
  );
}

// =============================================================================
// SHIFTS (VS#13)
// =============================================================================

export interface ShiftStatusResponse {
  shiftDate: string;
  shiftType: string;
  isClosed: boolean;
  closedAt?: string;
  p25Value: number | null;
  p50Value: number | null;
  closedBy?: { displayName: string };
  closedMode?: string;
}

// PR4: TWA Target für Karenz-Logik
export interface TwaTargetShift {
  shiftDateStr: string;
  shiftTypeCode: string;
  ruleName: string;
  startTime: string;
  endTime: string;
}

export interface TwaGraceInfo {
  isInGraceWindow: boolean;
  graceEndsAtMinute: number;
  previousEndMinute: number;
  nowMinute: number;
}

export interface TwaTargetResponse {
  current: TwaTargetShift | null;
  previous: TwaTargetShift | null;
  recommendedTarget: 'current' | 'previous' | null;
  graceInfo: TwaGraceInfo | null;
}

export async function getTwaTarget(): Promise<TwaTargetResponse> {
  return request<TwaTargetResponse>('/shifts/twa-target');
}

export interface CloseShiftRequest {
  shiftDate?: string;
  shiftType?: 'EARLY' | 'LATE' | 'SATURDAY';
  p25Value: number | null;
  p50Value: number | null;
}

export interface ShiftSummaryResponse {
  id: string;
  shiftDate: string;
  shiftType: string;
  p25Value: number | null;
  p50Value: number | null;
  closedAt: string;
  closedBy: { displayName: string };
  closedMode?: string;
}

export async function closeShift(
  stationId: string | null,
  data: CloseShiftRequest,
): Promise<ShiftSummaryResponse> {
  // Admin ohne Station: stationlosen Endpoint nutzen
  if (!stationId) {
    return request<ShiftSummaryResponse>('/shifts/close', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }
  return request<ShiftSummaryResponse>(`/stations/${stationId}/shifts/close`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function getShiftStatus(
  stationId: string | null,
  date?: string,
): Promise<ShiftStatusResponse> {
  const query = date ? `?date=${date}` : '';
  // Admin ohne Station: stationlosen Endpoint nutzen
  if (!stationId) {
    return request<ShiftStatusResponse>(`/shifts/status${query}`);
  }
  return request<ShiftStatusResponse>(`/stations/${stationId}/shifts/status${query}`);
}

// =============================================================================
// ADMIN - SHIFT SUMMARY
// =============================================================================

export interface ShiftSummaryResponse {
  exists: boolean;
  summary: {
    id: string;
    shiftDate: string;
    shiftType: string;
    p25Value: number | null;
    p50Value: number | null;
    recordedBy?: { displayName: string };
  } | null;
}

export async function getShiftSummary(
  date: string,
  shiftType: 'EARLY' | 'LATE' | 'SATURDAY',
): Promise<ShiftSummaryResponse> {
  return request<ShiftSummaryResponse>(`/admin/shift-summary?date=${date}&shiftType=${shiftType}`);
}

export async function updateShiftSummary(
  shiftDate: string,
  shiftType: 'EARLY' | 'LATE' | 'SATURDAY',
  p25Value: number | null,
  p50Value: number | null,
): Promise<{ summary: ShiftSummaryResponse['summary']; wasCreated: boolean }> {
  return request<{ summary: ShiftSummaryResponse['summary']; wasCreated: boolean }>(
    '/admin/shift-summary',
    {
      method: 'PATCH',
      body: JSON.stringify({ shiftDate, shiftType, p25Value, p50Value }),
    },
  );
}

// =============================================================================
// DASHBOARD
// =============================================================================

export interface DashboardKpis {
  today: {
    wsaCount: number;
    wsaKg: number;
    wsaCountTrend: number | null;
    wsaKgTrend: number | null;
    processingKg: number;
    activeDisruptions: number;
  };
  masterData: {
    users: number;
    customers: number;
    machines: number;
    stations: number;
  };
  disruptions: Array<{
    id: string;
    location: string | null;
    stationType: string | null;
    startedAt: string;
    durationMinutes: number;
  }>;
}

export async function getDashboardKpis(): Promise<DashboardKpis> {
  return request<DashboardKpis>('/dashboard/kpis');
}

// =============================================================================
// DASHBOARD V2 (Zeitraum-Auswahl)
// =============================================================================

export type DashboardRangeType = 'today' | 'currentShift' | 'yesterday' | 'lastShift';

export interface DashboardRangeInfo {
  rangeType: DashboardRangeType;
  label: string;
  isPartial: boolean;
  startDate: string;
  endDate: string;
  shiftType?: string;
  shiftName?: string;
}

export interface MachineKpiRow {
  machineId: string;
  machineName: string;
  wsaKg: number;
  entryCount: number;
  lastEntryAt: string | null;
  status: 'OK' | 'WARN' | 'CRITICAL';
}

export interface LineKpiRow {
  lineId: string;
  lineName: string;
  processingKg: number;
  entryCount: number;
  lastEntryAt: string | null;
  status: 'OK' | 'WARN' | 'CRITICAL';
}

export interface DashboardKpisV2 {
  range: DashboardRangeInfo;
  kpis: {
    wsaCount: number;
    wsaKg: number;
    processingKg: number;
    activeDisruptions: number;
    oldestDisruptionMinutes: number | null;
    machinesActive: number;
    machinesTotal: number;
    linesActive: number;
    linesTotal: number;
  };
  comparison: {
    wsaKgDelta: number | null;
    processingKgDelta: number | null;
  } | null;
  disruptions: Array<{
    id: string;
    location: string | null;
    stationType: string | null;
    startedAt: string;
    durationMinutes: number;
  }>;
  machineRows: MachineKpiRow[];
  lineRows: LineKpiRow[];
}

export async function getDashboardKpisV2(
  range: DashboardRangeType = 'today',
): Promise<DashboardKpisV2> {
  return request<DashboardKpisV2>(`/dashboard/v2?range=${range}`);
}

// =============================================================================
// SHIFTS (VS#16)
// =============================================================================

export interface CurrentShiftResponse {
  hasActiveShift: boolean;
  ruleId: string | null;
  ruleCode: string | null;
  ruleName: string | null;
  shiftTypeId: string | null;
  shiftTypeCode: string | null;
  shiftTypeName: string | null;
  shiftDate: string | null;
  shiftDateStr: string | null;
  startTime: string | null;
  endTime: string | null;
  weekday: number | null;
  // Boundary Grace (Kulanz-Minuten)
  isInGraceWindow: boolean;
  graceWindowType: 'START' | 'END' | null;
  boundaryGraceMinutes: number;
  /** Direkt nach dem Schichtwechsel: die eben beendete Schicht für die Ein-Tipp-Wahl. */
  nachzuegler: { shiftTypeCode: string; shiftTypeName: string; endTime: string } | null;
}

export async function getCurrentShift(): Promise<CurrentShiftResponse> {
  return request<CurrentShiftResponse>('/shifts/current');
}

// =============================================================================
// HEALTH
// =============================================================================

export async function healthCheck(): Promise<{ status: string }> {
  return request<{ status: string }>('/health');
}

// =============================================================================
// CORRECTIONS (Fehler melden)
// =============================================================================

export type CorrectionTargetType = 'WSA_ENTRY' | 'PROCESSING_ENTRY';
export type CorrectionCategory =
  'GEWICHT' | 'KUNDE' | 'MASCHINE' | 'PROGRAMM' | 'STRECKE' | 'KLASSIFIKATION' | 'SONSTIGES';

export interface CreateCorrectionRequestDto {
  targetType: CorrectionTargetType;
  targetId: string;
  category: CorrectionCategory;
  reasonText: string;
  suggestedCorrection?: string;
}

export interface CorrectionRequestResponse {
  id: string;
  status: string;
  createdAt: string;
  category: CorrectionCategory;
}

export async function createCorrectionRequest(
  stationId: string,
  data: CreateCorrectionRequestDto,
): Promise<CorrectionRequestResponse> {
  return request<CorrectionRequestResponse>(`/stations/${stationId}/corrections`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// =============================================================================
// CORRECTIONS V2 (Strukturiert - Modell A+B)
// =============================================================================

export interface CorrectionItemInput {
  field: string;
  newValue: unknown;
}

export interface CreateCorrectionRequestV2Dto {
  targetType: CorrectionTargetType;
  targetId: string;
  items: CorrectionItemInput[];
  workerComment?: string;
  presetCode?: string;
}

export interface CorrectionRequestV2Response {
  id: string;
  status: string;
  createdAt: string;
  correctionItems: Array<{
    field: string;
    oldValue: unknown;
    newValue: unknown;
  }>;
}

export async function createCorrectionRequestV2(
  stationId: string | null,
  data: CreateCorrectionRequestV2Dto,
): Promise<CorrectionRequestV2Response> {
  // Admin ohne Station: stationlosen Endpoint nutzen
  if (!stationId) {
    return request<CorrectionRequestV2Response>('/admin/corrections/v2', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }
  return request<CorrectionRequestV2Response>(`/stations/${stationId}/corrections/v2`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/** K2 Admin-Direktweg: Korrektur anlegen und annehmen in einem Schritt. */
export async function createCorrectionRequestV2Direct(
  data: CreateCorrectionRequestV2Dto,
): Promise<unknown> {
  return request('/admin/corrections/v2-direct', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/** K2: Antragsteller-Station zieht ihren offenen Korrektur-Antrag zurueck. */
export async function withdrawCorrection(
  stationId: string,
  correctionId: string,
): Promise<unknown> {
  return request(`/stations/${stationId}/corrections/${correctionId}/withdraw`, {
    method: 'POST',
  });
}

// =============================================================================
// ADMIN CORRECTIONS (Override)
// =============================================================================

export interface WsaOverridePayload {
  weightKg?: number;
  customerId?: string;
  machineId?: string;
  programId?: string;
  shiftDate?: string; // YYYY-MM-DD (date-only, Europe/Berlin Semantik)
  shiftTypeCodeSnap?: string; // Enum: EARLY, LATE, SATURDAY
}

export interface ProcessingOverridePayload {
  weightKg?: number;
  customerId?: string;
  customerGroupId?: string;
  lineId?: string;
  subLineId?: string;
  laundryClassId?: string;
  shiftDate?: string; // YYYY-MM-DD (date-only, Europe/Berlin Semantik)
  shiftTypeCodeSnap?: string; // Enum: EARLY, LATE, SATURDAY
}

export interface CreateOverrideDto {
  targetType: CorrectionTargetType;
  targetId: string;
  overridePayload: WsaOverridePayload | ProcessingOverridePayload;
  overrideReason: string;
  correctionRequestId?: string;
}

export interface OverrideResponse {
  id: string;
  targetType: CorrectionTargetType;
  targetId: string;
  overridePayload: Record<string, unknown>;
  overrideReason: string;
  overriddenBy: { id: string; displayName: string };
  overriddenAt: string;
}

export async function createOrUpdateOverride(data: CreateOverrideDto): Promise<OverrideResponse> {
  return request<OverrideResponse>('/admin/corrections/override', {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export interface RemoveOverrideResponse {
  success: boolean;
  message: string;
  targetType: CorrectionTargetType;
  targetId: string;
}

export async function removeOverride(
  targetType: CorrectionTargetType,
  targetId: string,
  reasonText: string,
): Promise<RemoveOverrideResponse> {
  return request<RemoveOverrideResponse>(`/admin/corrections/override/${targetType}/${targetId}`, {
    method: 'DELETE',
    body: JSON.stringify({ reasonText }),
  });
}

// =============================================================================
// GET SINGLE CORRECTION REQUEST (with entry context)
// =============================================================================

export interface CorrectionRequestDetail {
  id: string;
  targetType: string;
  targetId: string;
  correctionItems: Array<{
    field: string;
    oldValue: unknown;
    newValue: unknown;
    oldValueLabel?: string;
    newValueLabel?: string;
  }> | null;
  workerComment: string | null;
  category: string | null;
  reasonText: string | null;
  status: string;
  adminComment: string | null;
  createdAt: string;
  createdByStation: { id: string; name: string } | null;
  createdByUser: { id: string; displayName: string } | null;
  entryContext: {
    lineName?: string;
    subLineName?: string;
    machineName?: string;
    programName?: string;
    customerName?: string;
    customerGroupName?: string;
    weightKg?: number;
    recordedAt?: string;
    recordedByName?: string;
    laundryClassName?: string;
    articleGroupName?: string;
  };
}

/** Admin setzt den Antrags-Status (IN_REVIEW oder REJECTED) — K1: schließt auch Altformat-Anträge. */
export async function updateCorrectionRequestStatus(
  id: string,
  status: 'IN_REVIEW' | 'REJECTED',
  adminComment?: string,
): Promise<unknown> {
  return request(`/admin/corrections/${id}/status`, {
    method: 'PATCH',
    body: JSON.stringify({ status, adminComment }),
  });
}

export async function getCorrectionRequestDetail(id: string): Promise<CorrectionRequestDetail> {
  return request<CorrectionRequestDetail>(`/admin/corrections/${id}`, { method: 'GET' });
}

// =============================================================================
// V2 RESOLVE (Admin resolved einzelne Felder)
// =============================================================================

export interface ItemResolutionInput {
  field: string;
  resolution: 'ACCEPTED' | 'REJECTED' | 'MODIFIED';
  adminValue?: unknown;
  adminComment?: string;
}

export interface ResolveCorrectionV2Dto {
  itemResolutions: ItemResolutionInput[];
  adminComment?: string;
}

export async function resolveCorrectionRequestV2(
  correctionRequestId: string,
  data: ResolveCorrectionV2Dto,
): Promise<{ correctionRequest: { id: string; status: string; resolvedAt: string } }> {
  return request(`/admin/corrections/${correctionRequestId}/resolve-v2`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

// =============================================================================
// SIMULATE OVERRIDE (Preview Engine - read-only)
// =============================================================================

export interface SimulateOverrideResponse {
  effectivePayload: Record<string, unknown> | null;
  captureConfig: {
    id: string;
    requiresLaundryClass: boolean;
    requiresArticleGroup: boolean;
    allowedLaundryClasses: Array<{ id: string; name: string; requiresArticleGroup: boolean }>;
    allowedArticleGroups: Array<{ id: string; name: string }>;
  } | null;
  autoCleared: string[];
  validationError: { code: string; message: string; details?: Record<string, unknown> } | null;
}

export async function simulateOverride(
  targetType: CorrectionTargetType,
  targetId: string,
  proposedDelta: Record<string, unknown>,
  existingOverridePayload?: Record<string, unknown>,
): Promise<SimulateOverrideResponse> {
  return request<SimulateOverrideResponse>('/admin/corrections/simulate-override', {
    method: 'POST',
    body: JSON.stringify({ targetType, targetId, proposedDelta, existingOverridePayload }),
  });
}

// =============================================================================
// CORRECTIONS SUMMARY (Attention Area)
// =============================================================================

export interface CorrectionsSummaryLatestOpen {
  correctionRequestId: string;
  targetType: CorrectionTargetType;
  targetId: string;
  category: CorrectionCategory;
  reasonText: string;
  createdAt: string;
  createdByStationId: string | null;
  createdByStationName: string | null;
}

export interface CorrectionsSummaryResponse {
  openCount: number;
  overriddenCount: number;
  latestOpen: CorrectionsSummaryLatestOpen[];
}

export async function getWsaCorrectionsSummary(
  from: string,
  to: string,
  dateField: 'recordedAt' | 'shiftDate' = 'recordedAt',
): Promise<CorrectionsSummaryResponse> {
  return request<CorrectionsSummaryResponse>(
    `/reports/wsa/corrections-summary?from=${from}&to=${to}&dateField=${dateField}`,
  );
}

export async function getProcessingCorrectionsSummary(
  from: string,
  to: string,
  dateField: 'recordedAt' | 'shiftDate' = 'recordedAt',
): Promise<CorrectionsSummaryResponse> {
  return request<CorrectionsSummaryResponse>(
    `/reports/processing/corrections-summary?from=${from}&to=${to}&dateField=${dateField}`,
  );
}

// =============================================================================
// FEEDBACK SYSTEM
// =============================================================================

export type FeedbackCategory = 'BUG' | 'FEATURE' | 'OTHER';
export type FeedbackStatus = 'OPEN' | 'RUCKFRAGE' | 'DONE' | 'REJECTED';
export type FeedbackCommentType = 'MESSAGE' | 'SYSTEM_EVENT' | 'DEBUG_DATA';

export interface FeedbackComment {
  id: string;
  feedbackId: string;
  authorId: string | null;
  author: { id: string; displayName: string } | null;
  type: FeedbackCommentType;
  message: string | null;
  imagePath: string | null;
  metadata: string | null;
  createdAt: string;
}

export interface Feedback {
  id: string;
  number: number;
  title: string;
  category: FeedbackCategory;
  description: string | null;
  pageUrl: string | null;
  userAgent: string | null;
  status: FeedbackStatus;
  submittedById: string | null;
  submittedBy: { id: string; displayName: string } | null;
  stationId: string | null;
  station: { id: string; name: string | null; type: string } | null;
  comments: FeedbackComment[];
  createdAt: string;
  updatedAt: string;
}

// --- Worker Endpoints (Station-Auth) ---

export async function createStationFeedback(
  stationId: string,
  data: {
    title: string;
    category: FeedbackCategory;
    description?: string;
    pageUrl?: string;
    image?: File;
    debugData?: string;
  },
): Promise<Feedback> {
  const formData = new FormData();
  formData.append('title', data.title);
  formData.append('category', data.category);
  if (data.description) formData.append('description', data.description);
  if (data.pageUrl) formData.append('pageUrl', data.pageUrl);
  if (data.image) formData.append('image', data.image);
  if (data.debugData) formData.append('debugData', data.debugData);

  return requestFormData<Feedback>(`/stations/${stationId}/feedback`, formData);
}

export async function getStationFeedbacks(stationId: string): Promise<Feedback[]> {
  return request<Feedback[]>(`/stations/${stationId}/feedback`);
}

export async function addStationFeedbackComment(
  stationId: string,
  feedbackId: string,
  data: { message?: string; image?: File; debugData?: string },
): Promise<FeedbackComment> {
  const formData = new FormData();
  if (data.message) formData.append('message', data.message);
  if (data.image) formData.append('image', data.image);
  if (data.debugData) formData.append('debugData', data.debugData);

  return requestFormData<FeedbackComment>(
    `/stations/${stationId}/feedback/${feedbackId}/comments`,
    formData,
  );
}

// --- Admin Endpoints (Session-Auth) ---

export async function createAdminFeedback(data: {
  title: string;
  category: FeedbackCategory;
  description?: string;
  pageUrl?: string;
  image?: File;
  debugData?: string;
}): Promise<Feedback> {
  const formData = new FormData();
  formData.append('title', data.title);
  formData.append('category', data.category);
  if (data.description) formData.append('description', data.description);
  if (data.pageUrl) formData.append('pageUrl', data.pageUrl);
  if (data.image) formData.append('image', data.image);
  if (data.debugData) formData.append('debugData', data.debugData);

  return requestFormData<Feedback>('/feedback', formData);
}

export async function getAllFeedbacks(params?: {
  status?: FeedbackStatus;
  category?: FeedbackCategory;
}): Promise<Feedback[]> {
  const searchParams = new URLSearchParams();
  if (params?.status) searchParams.set('status', params.status);
  if (params?.category) searchParams.set('category', params.category);
  const qs = searchParams.toString();
  return request<Feedback[]>(`/feedback${qs ? `?${qs}` : ''}`);
}

export async function getMyFeedbacks(): Promise<Feedback[]> {
  return request<Feedback[]>('/feedback/mine');
}

export async function updateFeedbackStatus(
  feedbackId: string,
  status: FeedbackStatus,
): Promise<Feedback> {
  return request<Feedback>(`/feedback/${feedbackId}`, {
    method: 'PATCH',
    body: JSON.stringify({ status }),
  });
}

export async function addFeedbackComment(
  feedbackId: string,
  data: { message?: string; image?: File; debugData?: string },
): Promise<FeedbackComment> {
  const formData = new FormData();
  if (data.message) formData.append('message', data.message);
  if (data.image) formData.append('image', data.image);
  if (data.debugData) formData.append('debugData', data.debugData);

  return requestFormData<FeedbackComment>(`/feedback/${feedbackId}/comments`, formData);
}

export function getFeedbackImageUrl(imagePath: string, stationId?: string): string {
  if (stationId) {
    return `${API_BASE}/stations/${stationId}/feedback/images/${imagePath}`;
  }
  return `${API_BASE}/feedback/images/${imagePath}`;
}

// FormData-Request (fuer Bild-Upload, kein Content-Type Header - Browser setzt Boundary)
async function requestFormData<T>(path: string, formData: FormData): Promise<T> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 15000); // 15s fuer Upload

  try {
    const response = await fetch(`${API_BASE}${path}`, {
      method: 'POST',
      body: formData,
      credentials: 'include',
      signal: controller.signal,
    });

    const contentType = response.headers.get('content-type') || '';
    if (!contentType.includes('application/json')) {
      const text = await response.text();
      console.error('[api] Non-JSON response:', response.status, text.substring(0, 200));
      throw new ApiException(response.status, {
        error: 'Server-Fehler (kein JSON)',
        code: 'INVALID_RESPONSE',
      });
    }

    const data = await response.json();
    if (!response.ok) {
      throw new ApiException(response.status, data);
    }
    return data as T;
  } catch (err) {
    if (err instanceof Error && err.name === 'AbortError') {
      throw new ApiException(0, { error: 'Zeitueberschreitung', code: 'TIMEOUT' });
    }
    if (err instanceof ApiException) throw err;
    throw new ApiException(0, { error: 'Verbindungsfehler', code: 'NETWORK_ERROR' });
  } finally {
    clearTimeout(timeoutId);
  }
}
